import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, TrendingUp, Clock } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import api from '@/lib/api';
import { inr } from '@/lib/format';

const POPULAR = ['Vases', 'Wall Art', 'God Idols', 'Table Lamps', 'Fountains'];
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem('gaze_recent_searches') || '[]'); } catch { return []; }
};

export const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [recent, setRecent] = useState(readRecent());
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setSuggestions([]); }
    else setRecent(readRecent());
  }, [open]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 1) { setResults([]); setSuggestions([]); return; }
      try {
        const { data } = await api.get('/search', { params: { q } });
        setResults(data.results || []);
        setSuggestions(data.suggestions || []);
      } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (term) => {
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 6);
    localStorage.setItem('gaze_recent_searches', JSON.stringify(next));
    onOpenChange(false);
    navigate(`/shop?q=${encodeURIComponent(term)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[8%] max-w-2xl translate-y-0 gap-0 overflow-hidden p-0" data-testid="search-dialog">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search size={18} className="text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) go(q.trim()); }}
            placeholder="Search luxury decor, rooms, styles..."
            data-testid="search-input"
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <button onClick={() => onOpenChange(false)} aria-label="Close search"><X size={18} className="text-muted-foreground" /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {q.trim().length < 1 ? (
            <div className="space-y-5">
              {recent.length > 0 && (
                <div>
                  <p className="eyebrow mb-2 flex items-center gap-1"><Clock size={12} /> Recent searches</p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button key={r} onClick={() => go(r)} className="rounded-full border border-border bg-white px-3 py-1.5 text-xs hover:hairline-gold">{r}</button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="eyebrow mb-2 flex items-center gap-1"><TrendingUp size={12} /> Popular searches</p>
                <div className="flex flex-wrap gap-2">
                  {POPULAR.map((r) => (
                    <button key={r} onClick={() => go(r)} className="rounded-full border border-border bg-white px-3 py-1.5 text-xs hover:hairline-gold">{r}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {suggestions.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => go(s)} className="rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1.5 text-xs font-medium">{s}</button>
                  ))}
                </div>
              )}
              {results.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No results for "{q}"</p>
              ) : results.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { onOpenChange(false); navigate(`/product/${p.slug}`); }}
                  data-testid="search-result-item"
                  className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-[hsl(var(--gaze-ivory))]"
                >
                  <img src={(p.images || [])[0]} alt={p.name} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.category_name}</p>
                  </div>
                  <span className="text-sm font-semibold">{inr(p.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
